import uniqueId from 'lodash/uniqueId';
import findIndex from 'lodash/findIndex';
import slice from 'lodash/slice';
import eventbus from 'tiny-emitter/instance';
import Seatmap, { State } from '../seatmap';
import SVG from 'svg.js';

type HistoryState = {
  id: string;
  state: State & { viewbox?: SVG.ViewBoxLike };
  description: string;
  time: number;
};

type HistoryStateBox = {
  undo: HistoryState[];
  current: {
    id: string;
    state: State & { viewbox?: SVG.ViewBoxLike };
    description: string;
    time: number;
  };
  redo: HistoryState[];
};

export default class History {
  //TODO: Pause history for now until we figure out how to handle it
  paused = 1;
  state: HistoryStateBox;

  constructor(public seatmap: Seatmap) {
    this.state = History.createState();
  }

  get currentState() {
    return this.state.current;
  }

  get hasUndo() {
    return this.state.undo.length > 0;
  }

  get hasRedo() {
    return this.state.redo.length > 0;
  }

  get isPaused() {
    return this.paused > 0;
  }

  static formatState(state: State, description: string, seatmap?: Seatmap) {
    return {
      id: uniqueId(),
      state: {
        ...state,
        viewbox: seatmap?.app.svg.viewbox(),
      },
      description,
      time: Date.now(),
    };
  }

  static createState() {
    return {
      undo: [],
      current: History.formatState({ sections: [], bgImage: undefined, blocks: [], units: [] }, 'Initial state'),
      redo: [],
    };
  }

  log() {
    return [...this.state.undo, this.state.current];
  }

  pause() {
    this.paused += 1;
  }

  resume() {
    this.paused -= 1;
  }

  undo() {
    const current = this.state.undo.pop()!;
    const redo = this.state.current;
    this.state.redo.unshift(redo);
    this.state.current = current;
    eventbus.emit('seatmap:exitZone');
    return this.state.current;
  }

  redo() {
    const current = this.state.redo.shift()!;
    const undo = this.state.current;
    this.state.undo.push(undo);
    this.state.current = current;
    eventbus.emit('seatmap:exitZone');
    return this.state.current;
  }

  snapshot(state: State, description: string) {
    if (this.isPaused) return;
    const formattedState = History.formatState(state, description, this.seatmap);
    this.state.undo.push(this.state.current);
    this.state.current = formattedState;
    this.state.redo = [];

    // only keep last 100 states
    if (this.state.undo.length > 30) {
      this.state.undo.splice(0, this.state.undo.length - 30);
    }
  }

  jump(id: string) {
    if (this.state.current.id === id) return;
    const foundStateIdx = findIndex(this.state.undo, (state) => state.id === id);
    if (foundStateIdx !== -1) {
      this.state.current = this.state.undo[foundStateIdx];
      this.state.undo = slice(this.state.undo, 0, foundStateIdx);
      this.state.redo = [];
      return this.state.current;
    }
  }

  reset() {
    this.paused = 0;
    this.state.redo = [];
    this.state.undo = [];
  }

  destroy() {
    // this.state = undefined;
  }
}
